import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/api";

const fullUrl = (url) => !url ? "" : url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;

function ReelCard({ v, autoplay, active, onActivate }) {
  const cardRef = useRef(null);
  const videoRef = useRef(null);
  const [near, setNear] = useState(false);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setNear(true); obs.disconnect(); } }, { rootMargin: "250px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const el = cardRef.current;
    if (!el || !near) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && autoplay) onActivate(v.id);
    }, { threshold: 0.55 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [near, autoplay, v.id, onActivate]);

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid || !near) return;
    if (active && autoplay !== false) vid.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    else if (!active) { vid.pause(); setPlaying(false); }
  }, [active, near, autoplay]);

  const togglePlay = () => {
    if (active && playing) { videoRef.current?.pause(); setPlaying(false); }
    else onActivate(v.id);
  };

  return (
    <div className="snap-start shrink-0 w-[240px] sm:w-[300px] group" data-testid={`video-card-${v.id}`}>
      <div ref={cardRef} className="relative w-full aspect-[9/14] bg-[#111111] overflow-hidden">
        {near ? (
          <>
            <video ref={videoRef} src={fullUrl(v.url)} poster={fullUrl(v.thumbnail)} muted={muted} loop playsInline preload="metadata"
              data-testid={`video-player-${v.id}`} className="w-full h-full object-cover" />
            <button onClick={togglePlay} aria-label={playing ? "Pause reel" : "Play reel"} data-testid={`video-toggle-${v.id}`}
              className={`absolute inset-0 w-full h-full flex items-center justify-center transition-opacity ${playing ? "opacity-0 group-hover:opacity-100" : "opacity-100"}`}>
              <span className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-105">
                {playing ? <Pause size={20} className="text-[#111111]" fill="currentColor" /> : <Play size={20} className="text-[#111111] ml-0.5" fill="currentColor" />}
              </span>
            </button>
            <button onClick={() => setMuted(!muted)} aria-label={muted ? "Unmute" : "Mute"} data-testid={`video-mute-${v.id}`}
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/55 text-white flex items-center justify-center hover:bg-black/75 transition-colors">
              {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
            </button>
          </>
        ) : (
          v.thumbnail
            ? <img src={fullUrl(v.thumbnail)} alt={v.title} loading="lazy" className="w-full h-full object-cover" />
            : <div className="w-full h-full bg-[#1a1a1a]" />
        )}
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#111111]/70 to-transparent p-4 pointer-events-none">
          <p className="text-white font-serif text-lg leading-tight">{v.title}</p>
        </div>
      </div>
      {v.products?.length > 0 && (
        <Link to={`/products/${v.products[0].slug}`} data-testid={`shop-look-${v.id}`}
          className="mt-2.5 flex items-center justify-center border border-[#111111]/15 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] hover:bg-[#111111] hover:text-white transition-colors">
          Shop This Look
        </Link>
      )}
    </div>
  );
}

export default function VideoSection({ placement = "home", title = "Seen on TARISHQ", subtitle = "Shop the Look", autoplay = true }) {
  const [videos, setVideos] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const trackRef = useRef(null);

  useEffect(() => {
    api.get("/videos", { params: { placement } }).then(({ data }) => setVideos(data)).catch(() => setVideos([]));
  }, [placement]);

  if (!videos || videos.length === 0) return null;

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (el) el.scrollBy({ left: dir * (el.clientWidth * 0.7), behavior: "smooth" });
  };

  return (
    <section className="py-20 sm:py-28 bg-[#FAFAFA]" data-section="videos" data-testid="video-section">
      <div className="tq-container">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="tq-overline mb-2">{subtitle}</p>
            <h2 className="font-serif text-3xl sm:text-4xl">{title}</h2>
          </div>
          {videos.length > 1 && (
            <div className="hidden sm:flex gap-2">
              <button onClick={() => scrollBy(-1)} aria-label="Previous videos" data-testid="video-prev"
                className="w-10 h-10 border border-[#EAEAEA] flex items-center justify-center hover:border-[#C59B27] hover:text-[#A8821B] transition-colors"><ChevronLeft size={18} /></button>
              <button onClick={() => scrollBy(1)} aria-label="Next videos" data-testid="video-next"
                className="w-10 h-10 border border-[#EAEAEA] flex items-center justify-center hover:border-[#C59B27] hover:text-[#A8821B] transition-colors"><ChevronRight size={18} /></button>
            </div>
          )}
        </div>

        <div ref={trackRef} className="flex gap-4 sm:gap-6 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2" data-testid="video-track">
          {videos.map((v) => (
            <ReelCard key={v.id} v={v} autoplay={autoplay} active={activeId === v.id} onActivate={setActiveId} />
          ))}
        </div>
      </div>
    </section>
  );
}
